import { useState } from 'react'
import { Chip, Button, Select, Input, Avatar } from '@/components/ui'
import { Download, Star } from 'lucide-react'
import { fmtMoney } from '@/lib/utils'
import { cn } from '@/lib/utils'

type Resultado = 'resuelto' | 'compromiso' | 'escalado' | 'no_contesto'

interface Row {
  cliente: string
  cedula: string
  cuandoRel: string
  razon: string
  razonChip: 'blue' | 'emerald' | 'rose' | 'amber' | 'violet'
  agente: string
  tiempoMin: number
  resultado: Resultado
  resultadoDetalle?: string
  rating: number | null
}

const mock: Row[] = [
  { cliente: 'Ligia Mieles', cedula: '0906368113', cuandoRel: 'hace 32 min', razon: '💼 Plan pago', razonChip: 'blue', agente: 'Ana Vera', tiempoMin: 18, resultado: 'compromiso', resultadoDetalle: '$59.74', rating: 5 },
  { cliente: 'Danny Grefa', cedula: '1550091761', cuandoRel: 'hace 1h', razon: '💬 Pidió asesor', razonChip: 'emerald', agente: 'Luis Mora', tiempoMin: 8, resultado: 'resuelto', rating: 5 },
  { cliente: 'Susan Gavica', cedula: '0915068415', cuandoRel: 'hace 1h 30min', razon: '📸 Comprobante', razonChip: 'rose', agente: 'Ana Vera', tiempoMin: 12, resultado: 'resuelto', rating: 4 },
  { cliente: 'Patricio Sumbaña', cedula: '0500672712', cuandoRel: 'hace 2h', razon: '💼 Refinanciamiento', razonChip: 'amber', agente: 'María Gómez', tiempoMin: 45, resultado: 'escalado', resultadoDetalle: 'a jefe', rating: 3 },
  { cliente: 'Vicenta Rodríguez', cedula: '0906355532', cuandoRel: 'hace 3h', razon: '🤖 IA no pudo', razonChip: 'violet', agente: 'Luis Mora', tiempoMin: 22, resultado: 'no_contesto', rating: null },
  { cliente: 'Carlos Macías', cedula: '0920115588', cuandoRel: 'hace 4h', razon: '💬 Pidió asesor', razonChip: 'emerald', agente: 'Ana Vera', tiempoMin: 6, resultado: 'resuelto', rating: 5 },
  { cliente: 'Jorge Intriago', cedula: '0908112233', cuandoRel: 'hace 5h', razon: '🤖 IA no pudo', razonChip: 'violet', agente: 'María Gómez', tiempoMin: 28, resultado: 'compromiso', resultadoDetalle: '$145.00', rating: 4 },
  { cliente: 'Rosa Bonilla', cedula: '0917556677', cuandoRel: 'hace 6h', razon: '📸 Comprobante', razonChip: 'rose', agente: 'Luis Mora', tiempoMin: 15, resultado: 'resuelto', rating: 5 },
]

const resultadoChip: Record<Resultado, { label: string; color: 'emerald' | 'amber' | 'rose' | 'default' }> = {
  resuelto: { label: '✓ Resuelto', color: 'emerald' },
  compromiso: { label: '💰 Compromiso', color: 'emerald' },
  escalado: { label: '↑ Escalado', color: 'amber' },
  no_contesto: { label: '📵 No contestó', color: 'default' },
}

export function Resueltos() {
  const [agente, setAgente] = useState('todos')
  const [resultado, setResultado] = useState<string>('todos')

  const filtradas = mock.filter(r => {
    if (agente !== 'todos' && r.agente !== agente) return false
    if (resultado !== 'todos' && r.resultado !== resultado) return false
    return true
  })

  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
        <Input label="Desde" type="date" defaultValue="2026-10-01" className="w-40" />
        <Input label="Hasta" type="date" defaultValue="2026-10-02" className="w-40" />
        <div className="w-px h-10 bg-slate-200" />
        <Select label="Agente" value={agente} onChange={e => setAgente(e.target.value)} className="w-48">
          <option value="todos">Todos los agentes</option>
          <option>Ana Vera</option>
          <option>Luis Mora</option>
          <option>María Gómez</option>
          <option>Carlos Vera</option>
        </Select>
        <Select label="Resultado" value={resultado} onChange={e => setResultado(e.target.value)} className="w-48">
          <option value="todos">Todos los resultados</option>
          <option value="resuelto">✓ Resuelto</option>
          <option value="compromiso">💰 Compromiso</option>
          <option value="escalado">↑ Escalado</option>
          <option value="no_contesto">📵 No contestó</option>
        </Select>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-slate-500"><span className="font-semibold text-navy-700">{filtradas.length}</span> resueltos</span>
          <Button variant="outline" size="sm"><Download className="w-3.5 h-3.5" /> Export CSV</Button>
        </div>
      </div>

      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
            <tr>
              <th className="text-left px-4 py-2">Cliente</th>
              <th className="text-left px-4 py-2">Razón</th>
              <th className="text-left px-4 py-2">Agente</th>
              <th className="text-right px-4 py-2">Tiempo</th>
              <th className="text-left px-4 py-2">Resultado</th>
              <th className="text-left px-4 py-2">Rating</th>
              <th className="text-right px-4 py-2">Acción</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtradas.map((r, i) => {
              const res = resultadoChip[r.resultado]
              return (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Avatar name={r.cliente} size="sm" />
                      <div>
                        <div className="font-medium text-navy-700">{r.cliente}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{r.cedula} · {r.cuandoRel}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Chip variant={r.razonChip}>{r.razon}</Chip>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{r.agente}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{r.tiempoMin} min</td>
                  <td className="px-4 py-3">
                    <Chip variant={res.color}>
                      {res.label} {r.resultadoDetalle && <span className="font-mono ml-1">{r.resultadoDetalle}</span>}
                    </Chip>
                  </td>
                  <td className="px-4 py-3">
                    {r.rating !== null ? (
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }).map((_, j) => (
                          <Star key={j} className={cn('w-3.5 h-3.5', j < r.rating! ? 'fill-amber-400 text-amber-400' : 'text-slate-200')} />
                        ))}
                      </div>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button className="text-amber-600 hover:underline text-xs font-semibold">Ver →</button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>
    </div>
  )
}
